import React, { useCallback, useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Tags, Save, RefreshCw, AlertTriangle, Plus, Trash2, ListChecks } from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
// VALUE imports via the dist subpath, never the '@valutten/shared-schemas'
// barrel (it pulls Node crypto into the browser bundle). Types are erased.
import {
  CLASSIFICATION_DIMENSIONS,
  CLASSIFICATION_DIMENSION_QUESTIONS,
  DEFAULT_CLASSIFICATION_OPTIONS,
  DEFAULT_CLASSIFICATION_SERVICE_CATEGORIES,
  optionList,
} from '@valutten/shared-schemas/dist/leadClassification';
import type { ClassificationDimension, ClassificationOptions } from '@valutten/shared-schemas';
import { functions } from '../../lib/firebase';
import { PageHeader } from '../../components/common/PageHeader';

type OutletCtx = {
  userData?: { uid?: string; companyId?: string; role?: string };
};

interface ServiceCategory {
  id: string;
  label: string;
  order: number;
  active: boolean;
}

interface DealType {
  id: string;
  label: string;
  order: number;
}

interface Taxonomy {
  serviceCategories: ServiceCategory[];
  dealTypes: Record<string, DealType[]>;
  // Per service category, the options for the five classification questions.
  // A category missing here uses VALUTTEN's defaults when it is the mortgage
  // category (finance / home_loan) and asks nothing otherwise.
  classification: Record<string, ClassificationOptions>;
  isDefault?: boolean;
}

type TaxonomyResponse = Omit<Taxonomy, 'classification'> & { classification?: Record<string, ClassificationOptions> | null };

// A stored category written before a dimension existed (loanAmount) lacks
// its list; optionList reads it as empty, so every dimension is present here.
function cloneOptions(o: Partial<ClassificationOptions>): ClassificationOptions {
  const out = {} as ClassificationOptions;
  for (const dim of CLASSIFICATION_DIMENSIONS) out[dim] = optionList(o, dim).map((x) => ({ ...x }));
  return out;
}

const EMPTY_OPTIONS: ClassificationOptions = {
  loanPurpose: [], propertyUse: [], borrowerSituation: [], loanStructure: [], loanAmount: [],
};

function normaliseClassification(
  c: Record<string, Partial<ClassificationOptions>> | null | undefined,
): Record<string, ClassificationOptions> {
  const out: Record<string, ClassificationOptions> = {};
  for (const [catId, opts] of Object.entries(c ?? {})) out[catId] = cloneOptions(opts ?? {});
  return out;
}

const ID_PATTERN = /^[a-z][a-z0-9_]{0,40}$/;

const LeadTaxonomyPage: React.FC = () => {
  const { userData } = useOutletContext<OutletCtx>();
  const companyId = userData?.companyId ?? '';
  const role = userData?.role ?? '';
  // Marketing (Lead-Capture operator) is granted access to the 4 Lead-Capture
  // config tabs alongside super_admin / administrator (marketing RBAC §1 GRANT).
  const isAdmin = role === 'super_admin' || role === 'administrator' || role === 'marketing';

  const [taxonomy, setTaxonomy] = useState<Taxonomy>({
    serviceCategories: [],
    dealTypes: {},
    classification: {},
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [seeded, setSeeded] = useState(false);
  // True only after the taxonomy has actually come back from the server.
  // The page starts from an EMPTY taxonomy, so a failed load left Save
  // enabled over an empty form: one click wiped the tenant's whole
  // taxonomy, which drives lead routing and every category dropdown.
  const [loadedOk, setLoadedOk] = useState(false);

  // Load via callable (returns DEFAULT_LEAD_TAXONOMY when no doc exists)
  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    (async () => {
      try {
        const fn = httpsCallable<unknown, TaxonomyResponse>(functions, 'leadCaptureAction');
        const r = await fn({ action: 'getLeadTaxonomy', companyId });
        if (cancelled) return;
        setTaxonomy({
          serviceCategories: r.data.serviceCategories ?? [],
          dealTypes: r.data.dealTypes ?? {},
          classification: normaliseClassification(r.data.classification),
        });
        setSeeded(!!r.data.isDefault);
        setLoadedOk(true);
      } catch (err) {
        console.error('[LeadTaxonomyPage] load failed', err);
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  const save = useCallback(async () => {
    if (!companyId) return;
    // Never write over a taxonomy we failed to read.
    if (!loadedOk) {
      setError('The current taxonomy has not loaded yet. Reload the page before saving.');
      return;
    }
    const ids = taxonomy.serviceCategories.map((c) => c.id.trim());
    if (ids.some((id) => !id)) {
      setError('Every service category needs an id.');
      return;
    }
    const duplicate = ids.find((id, i) => ids.indexOf(id) !== i);
    if (duplicate) {
      setError(`Duplicate category id "${duplicate}" - ids must be unique.`);
      return;
    }
    if (taxonomy.serviceCategories.length === 0) {
      setError('A taxonomy needs at least one service category.');
      return;
    }
    for (const [catId, opts] of Object.entries(taxonomy.classification)) {
      for (const dim of CLASSIFICATION_DIMENSIONS) {
        const optIds = optionList(opts, dim).map((o) => o.id.trim());
        if (optIds.some((id) => !ID_PATTERN.test(id))) {
          setError(`Every ${CLASSIFICATION_DIMENSION_LABEL[dim].toLowerCase()} option under "${catId}" needs a valid id.`);
          return;
        }
        const dup = optIds.find((id, i) => optIds.indexOf(id) !== i);
        if (dup) {
          setError(`Duplicate option id "${dup}" under "${catId}".`);
          return;
        }
        if (optionList(opts, dim).some((o) => !o.label.trim())) {
          setError(`Every option under "${catId}" needs a label.`);
          return;
        }
      }
    }
    setSaving(true);
    setError(null);
    try {
      const fn = httpsCallable<unknown, { success: boolean }>(functions, 'leadCaptureAction');
      await fn({ action: 'saveLeadTaxonomy', companyId, taxonomy });
      setSavedAt(Date.now());
      setSeeded(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }, [companyId, taxonomy, loadedOk]);

  const addCategory = useCallback(() => {
    setTaxonomy((t) => {
      const nextOrder = (t.serviceCategories[t.serviceCategories.length - 1]?.order ?? 0) + 1;
      return {
        ...t,
        serviceCategories: [
          ...t.serviceCategories,
          { id: '', label: 'New category', order: nextOrder, active: true },
        ],
      };
    });
  }, []);

  const updateCategory = useCallback((idx: number, patch: Partial<ServiceCategory>) => {
    setTaxonomy((t) => {
      const next = [...t.serviceCategories];
      const previousId = next[idx]?.id;
      next[idx] = { ...next[idx], ...patch };

      // dealTypes is keyed by category id, so renaming an id ORPHANS its
      // deal types: they stayed under the old key, which no category
      // referenced any more, and the renamed category silently lost them.
      // Re-key alongside the rename.
      let nextDealTypes = t.dealTypes;
      const newId = patch.id;
      if (newId !== undefined && previousId !== undefined && newId !== previousId) {
        nextDealTypes = { ...t.dealTypes };
        if (previousId in nextDealTypes) {
          const carried = nextDealTypes[previousId];
          delete nextDealTypes[previousId];
          if (newId) nextDealTypes[newId] = carried;
        }
      }
      // classification is keyed the same way: carry it across a rename.
      let nextClassification = t.classification;
      if (newId !== undefined && previousId !== undefined && newId !== previousId && previousId in t.classification) {
        nextClassification = { ...t.classification };
        const carried = nextClassification[previousId];
        delete nextClassification[previousId];
        if (newId) nextClassification[newId] = carried;
      }
      return { serviceCategories: next, dealTypes: nextDealTypes, classification: nextClassification };
    });
  }, []);

  const removeCategory = useCallback((idx: number) => {
    setTaxonomy((t) => {
      const removed = t.serviceCategories[idx];
      const nextCats = t.serviceCategories.filter((_, i) => i !== idx);
      const nextDealTypes = { ...t.dealTypes };
      if (removed?.id) delete nextDealTypes[removed.id];
      const nextClassification = { ...t.classification };
      if (removed?.id) delete nextClassification[removed.id];
      return { serviceCategories: nextCats, dealTypes: nextDealTypes, classification: nextClassification };
    });
  }, []);

  const addDealType = useCallback((catId: string) => {
    setTaxonomy((t) => {
      const list = t.dealTypes[catId] ?? [];
      const nextOrder = (list[list.length - 1]?.order ?? 0) + 1;
      return {
        ...t,
        dealTypes: {
          ...t.dealTypes,
          [catId]: [...list, { id: '', label: 'New deal type', order: nextOrder }],
        },
      };
    });
  }, []);

  const updateDealType = useCallback((catId: string, idx: number, patch: Partial<DealType>) => {
    setTaxonomy((t) => {
      const list = [...(t.dealTypes[catId] ?? [])];
      list[idx] = { ...list[idx], ...patch };
      return { ...t, dealTypes: { ...t.dealTypes, [catId]: list } };
    });
  }, []);

  const removeDealType = useCallback((catId: string, idx: number) => {
    setTaxonomy((t) => {
      const list = (t.dealTypes[catId] ?? []).filter((_, i) => i !== idx);
      return { ...t, dealTypes: { ...t.dealTypes, [catId]: list } };
    });
  }, []);

  // ── Classification options (five questions per service) ──────────────
  const setCategoryOptions = useCallback((catId: string, next: ClassificationOptions | null) => {
    setTaxonomy((t) => {
      const classification = { ...t.classification };
      if (next) classification[catId] = next;
      else delete classification[catId];
      return { ...t, classification };
    });
  }, []);

  const updateOption = useCallback(
    (catId: string, dim: ClassificationDimension, idx: number, patch: Partial<{ id: string; label: string; order: number; active: boolean }>) => {
      setTaxonomy((t) => {
        const opts = t.classification[catId];
        if (!opts) return t;
        const list = [...optionList(opts, dim)];
        list[idx] = { ...list[idx], ...patch };
        return { ...t, classification: { ...t.classification, [catId]: { ...opts, [dim]: list } } };
      });
    },
    [],
  );

  const addOption = useCallback((catId: string, dim: ClassificationDimension) => {
    setTaxonomy((t) => {
      const opts = t.classification[catId] ?? cloneOptions(EMPTY_OPTIONS);
      const list = optionList(opts, dim);
      const nextOrder = (list[list.length - 1]?.order ?? 0) + 1;
      return {
        ...t,
        classification: {
          ...t.classification,
          [catId]: { ...opts, [dim]: [...list, { id: '', label: '', order: nextOrder, active: true }] },
        },
      };
    });
  }, []);

  const removeOption = useCallback((catId: string, dim: ClassificationDimension, idx: number) => {
    setTaxonomy((t) => {
      const opts = t.classification[catId];
      if (!opts) return t;
      return {
        ...t,
        classification: { ...t.classification, [catId]: { ...opts, [dim]: optionList(opts, dim).filter((_, i) => i !== idx) } },
      };
    });
  }, []);

  if (!isAdmin) {
    return (
      <div className="space-y-6">
        <PageHeader icon={Tags} title="Lead Taxonomy" subtitle="super_admin / administrator only" />
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-4 text-amber-300 text-sm">
          You don&apos;t have permission to view this page.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Tags}
        title="Lead Taxonomy"
        subtitle="Define the service categories and deal types your business handles. Routing rules + lead-capture forms reference these IDs."
        badge="Beta"
        actions={
          <button
            onClick={save}
            disabled={saving || loading || !loadedOk}
            title={loadedOk ? undefined : 'Waiting for the current taxonomy to load'}
            className="px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50"
          >
            {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? 'Saving…' : 'Save Taxonomy'}
          </button>
        }
      />

      {error && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-red-300 text-sm flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" /><span>{error}</span>
        </div>
      )}
      {seeded && (
        <div className="rounded-lg border border-blue-500/30 bg-blue-500/10 p-3 text-blue-300 text-sm">
          You&apos;re viewing the default taxonomy seed. Customise + save to make it yours.
        </div>
      )}
      {savedAt && Date.now() - savedAt < 3000 && (
        <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3 text-emerald-300 text-sm">
          Taxonomy saved.
        </div>
      )}

      <div className="rounded-lg border border-gray-700 bg-gray-900/40 p-5">
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-sm uppercase tracking-wider text-gray-400 font-semibold">
            Service Categories
          </h3>
          <button
            onClick={addCategory}
            className="px-3 py-1.5 rounded-md bg-gray-800 border border-gray-700 hover:bg-gray-700 text-white text-xs inline-flex items-center gap-1"
          >
            <Plus className="w-3 h-3" /> Add Category
          </button>
        </div>

        {taxonomy.serviceCategories.length === 0 ? (
          <div className="text-center text-gray-400 text-sm py-6">No categories yet.</div>
        ) : (
          <div className="space-y-4">
            {taxonomy.serviceCategories.map((cat, idx) => {
              const idValid = cat.id === '' || ID_PATTERN.test(cat.id);
              const dealTypes = taxonomy.dealTypes[cat.id] ?? [];
              return (
                <div key={idx} className="rounded-md border border-gray-800 bg-gray-950/40 p-3">
                  <div className="grid grid-cols-12 gap-2 mb-2 items-center">
                    <input
                      type="text"
                      placeholder="id (e.g. finance)"
                      value={cat.id}
                      onChange={(e) => updateCategory(idx, { id: e.target.value })}
                      className={`col-span-3 bg-gray-800 border ${idValid ? 'border-gray-700' : 'border-red-500'} text-white rounded-md px-2 py-1 text-xs font-mono`}
                    />
                    <input
                      type="text"
                      placeholder="Label (e.g. Finance)"
                      value={cat.label}
                      onChange={(e) => updateCategory(idx, { label: e.target.value })}
                      className="col-span-5 bg-gray-800 border border-gray-700 text-white rounded-md px-2 py-1 text-xs"
                    />
                    <input
                      type="number"
                      min={0}
                      placeholder="Order"
                      value={cat.order}
                      onChange={(e) => updateCategory(idx, { order: parseInt(e.target.value, 10) || 0 })}
                      className="col-span-1 bg-gray-800 border border-gray-700 text-white rounded-md px-2 py-1 text-xs"
                    />
                    <label className="col-span-2 text-xs text-gray-400 inline-flex items-center gap-1">
                      <input
                        type="checkbox"
                        checked={cat.active}
                        onChange={(e) => updateCategory(idx, { active: e.target.checked })}
                        className="accent-blue-500 w-3.5 h-3.5"
                      />
                      Active
                    </label>
                    <button
                      onClick={() => removeCategory(idx)}
                      className="col-span-1 text-gray-400 hover:text-red-400 p-1 justify-self-end"
                      title="Remove category"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>

                  {/* Deal types nested */}
                  {cat.id && idValid && (
                    <div className="ml-4 mt-2 pl-3 border-l-2 border-gray-800 space-y-1.5">
                      <div className="flex justify-between items-center">
                        <span className="text-[11px] uppercase tracking-wider text-gray-500">
                          Deal types under {cat.label}
                        </span>
                        <button
                          onClick={() => addDealType(cat.id)}
                          className="text-[11px] text-blue-400 hover:text-blue-300 inline-flex items-center gap-1"
                        >
                          <Plus className="w-3 h-3" /> Add deal type
                        </button>
                      </div>
                      {dealTypes.length === 0 && (
                        <div className="text-[11px] text-gray-500 italic">
                          (no sub-types — leads under this category route by category alone)
                        </div>
                      )}
                      {dealTypes.length > 0 && (
                        <div className="text-[11px] text-gray-500">
                          Deal types are the earlier single-answer routing field. New forms ask the classification questions below.
                        </div>
                      )}
                      {dealTypes.map((dt, dtIdx) => {
                        const dtIdValid = dt.id === '' || ID_PATTERN.test(dt.id);
                        return (
                          <div key={dtIdx} className="grid grid-cols-12 gap-2 items-center">
                            <input
                              type="text"
                              placeholder="id (e.g. fhb)"
                              value={dt.id}
                              onChange={(e) => updateDealType(cat.id, dtIdx, { id: e.target.value })}
                              className={`col-span-3 bg-gray-800 border ${dtIdValid ? 'border-gray-700' : 'border-red-500'} text-white rounded-md px-2 py-1 text-xs font-mono`}
                            />
                            <input
                              type="text"
                              placeholder="Label"
                              value={dt.label}
                              onChange={(e) => updateDealType(cat.id, dtIdx, { label: e.target.value })}
                              className="col-span-7 bg-gray-800 border border-gray-700 text-white rounded-md px-2 py-1 text-xs"
                            />
                            <input
                              type="number"
                              min={0}
                              value={dt.order}
                              onChange={(e) => updateDealType(cat.id, dtIdx, { order: parseInt(e.target.value, 10) || 0 })}
                              className="col-span-1 bg-gray-800 border border-gray-700 text-white rounded-md px-2 py-1 text-xs"
                            />
                            <button
                              onClick={() => removeDealType(cat.id, dtIdx)}
                              className="col-span-1 text-gray-400 hover:text-red-400 p-1 justify-self-end"
                              title="Remove deal type"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {cat.id && idValid && (
                    <ClassificationOptionsEditor
                      categoryId={cat.id}
                      categoryLabel={cat.label}
                      options={taxonomy.classification[cat.id] ?? null}
                      onSet={setCategoryOptions}
                      onUpdate={updateOption}
                      onAdd={addOption}
                      onRemove={removeOption}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {loading && <div className="text-center text-gray-400 text-sm py-4">Loading taxonomy…</div>}

      <div className="rounded-lg border border-gray-700 bg-gray-900/30 p-4 text-xs text-gray-400">
        <p className="font-semibold text-gray-300 mb-1">How taxonomy IDs are used</p>
        <ul className="list-disc ml-5 space-y-0.5">
          <li>Lead capture webhook accepts <code className="text-gray-300">serviceCategory</code> + <code className="text-gray-300">dealType</code> body fields, and the five classification answers as <code className="text-gray-300">classification</code>.</li>
          <li>Classification answers are option ids. Unknown ids are dropped from the lead, never stored.</li>
          <li>Routing rules cascade matches on these IDs (<a href="/leads/routing" className="text-blue-400 hover:underline">/leads/routing</a>).</li>
          <li>IDs must be lowercase + alphanumeric + underscore. Labels are display-only.</li>
          <li>Removing a category here doesn&apos;t delete leads already tagged with that ID — they fall through to less-specific cascade tiers.</li>
        </ul>
      </div>
    </div>
  );
};

const CLASSIFICATION_DIMENSION_LABEL: Record<ClassificationDimension, string> = {
  loanPurpose: 'Loan purpose',
  propertyUse: 'Property use',
  borrowerSituation: 'Borrower situation',
  loanStructure: 'Loan structure',
  loanAmount: 'Loan amount',
};

interface ClassificationOptionsEditorProps {
  categoryId: string;
  categoryLabel: string;
  /** The tenant's own options for this category, or null when none are saved. */
  options: ClassificationOptions | null;
  onSet: (catId: string, next: ClassificationOptions | null) => void;
  onUpdate: (
    catId: string,
    dim: ClassificationDimension,
    idx: number,
    patch: Partial<{ id: string; label: string; order: number; active: boolean }>,
  ) => void;
  onAdd: (catId: string, dim: ClassificationDimension) => void;
  onRemove: (catId: string, dim: ClassificationDimension, idx: number) => void;
}

/**
 * The five classification questions for one service category. With no saved
 * options the mortgage category shows VALUTTEN's defaults read-only (they are
 * what applies) and offers Customise, which copies them in for editing;
 * Reset returns the category to the defaults. Other categories start empty
 * (no questions asked) until options are added.
 */
const ClassificationOptionsEditor: React.FC<ClassificationOptionsEditorProps> = ({
  categoryId, categoryLabel, options, onSet, onUpdate, onAdd, onRemove,
}) => {
  const hasDefaults = DEFAULT_CLASSIFICATION_SERVICE_CATEGORIES.includes(categoryId);
  const usingDefaults = !options && hasDefaults;
  const shown = options ?? (hasDefaults ? DEFAULT_CLASSIFICATION_OPTIONS : null);

  return (
    <div className="ml-4 mt-3 pl-3 border-l-2 border-gray-800 space-y-2" data-testid={`classification-editor-${categoryId}`}>
      <div className="flex justify-between items-center gap-2">
        <span className="text-[11px] uppercase tracking-wider text-gray-500 inline-flex items-center gap-1">
          <ListChecks className="w-3 h-3" /> Classification questions for {categoryLabel}
        </span>
        {usingDefaults && (
          <button
            type="button"
            onClick={() => onSet(categoryId, cloneOptions(DEFAULT_CLASSIFICATION_OPTIONS))}
            className="text-[11px] text-blue-400 hover:text-blue-300"
          >
            Customise
          </button>
        )}
        {options && hasDefaults && (
          <button
            type="button"
            onClick={() => onSet(categoryId, null)}
            className="text-[11px] text-gray-400 hover:text-gray-200"
          >
            Reset to VALUTTEN defaults
          </button>
        )}
      </div>
      {usingDefaults && (
        <div className="text-[11px] text-gray-500">Using VALUTTEN defaults. Customise to rename, reorder, hide or add options.</div>
      )}
      {!shown && (
        <div className="text-[11px] text-gray-500 italic">
          (no questions asked for this service){' '}
          <button type="button" onClick={() => onAdd(categoryId, 'loanPurpose')} className="text-blue-400 hover:text-blue-300 not-italic">
            Add options
          </button>
        </div>
      )}
      {shown && CLASSIFICATION_DIMENSIONS.map((dim) => (
        <div key={dim} className="space-y-1">
          <div className="flex justify-between items-center">
            <span className="text-[11px] text-gray-400">
              {CLASSIFICATION_DIMENSION_LABEL[dim]}
              <span className="text-gray-600"> · {CLASSIFICATION_DIMENSION_QUESTIONS[dim]}{dim === 'borrowerSituation' ? ' (several allowed)' : ''}</span>
            </span>
            {!usingDefaults && (
              <button
                type="button"
                onClick={() => onAdd(categoryId, dim)}
                className="text-[11px] text-blue-400 hover:text-blue-300 inline-flex items-center gap-1"
              >
                <Plus className="w-3 h-3" /> Add option
              </button>
            )}
          </div>
          {optionList(shown, dim).length === 0 && (
            <div className="text-[11px] text-gray-600 italic">
              (not asked)
              {/* A category customised before loan amount existed has no bands;
                  offer the standard ones instead of typing five rows. */}
              {dim === 'loanAmount' && options && (
                <>
                  {' '}
                  <button
                    type="button"
                    onClick={() => onSet(categoryId, { ...cloneOptions(options), loanAmount: cloneOptions(DEFAULT_CLASSIFICATION_OPTIONS).loanAmount })}
                    className="text-blue-400 hover:text-blue-300 not-italic"
                  >
                    Use the standard loan size bands
                  </button>
                </>
              )}
            </div>
          )}
          {optionList(shown, dim).map((o, idx) => {
            const idOk = o.id === '' || ID_PATTERN.test(o.id);
            return (
              <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                <input
                  type="text"
                  aria-label={`${CLASSIFICATION_DIMENSION_LABEL[dim]} option id`}
                  placeholder="id (e.g. refinance)"
                  value={o.id}
                  disabled={usingDefaults}
                  onChange={(e) => onUpdate(categoryId, dim, idx, { id: e.target.value })}
                  className={`col-span-3 bg-gray-800 border ${idOk ? 'border-gray-700' : 'border-red-500'} text-white rounded-md px-2 py-1 text-xs font-mono disabled:opacity-60`}
                />
                <input
                  type="text"
                  aria-label={`${CLASSIFICATION_DIMENSION_LABEL[dim]} option label`}
                  placeholder="Label"
                  value={o.label}
                  disabled={usingDefaults}
                  onChange={(e) => onUpdate(categoryId, dim, idx, { label: e.target.value })}
                  className="col-span-5 bg-gray-800 border border-gray-700 text-white rounded-md px-2 py-1 text-xs disabled:opacity-60"
                />
                <input
                  type="number"
                  min={0}
                  aria-label={`${CLASSIFICATION_DIMENSION_LABEL[dim]} option order`}
                  value={o.order}
                  disabled={usingDefaults}
                  onChange={(e) => onUpdate(categoryId, dim, idx, { order: parseInt(e.target.value, 10) || 0 })}
                  className="col-span-1 bg-gray-800 border border-gray-700 text-white rounded-md px-2 py-1 text-xs disabled:opacity-60"
                />
                <label className="col-span-2 text-xs text-gray-400 inline-flex items-center gap-1">
                  <input
                    type="checkbox"
                    checked={o.active !== false}
                    disabled={usingDefaults}
                    onChange={(e) => onUpdate(categoryId, dim, idx, { active: e.target.checked })}
                    className="accent-blue-500 w-3.5 h-3.5"
                  />
                  Active
                </label>
                {!usingDefaults && (
                  <button
                    type="button"
                    onClick={() => onRemove(categoryId, dim, idx)}
                    className="col-span-1 text-gray-400 hover:text-red-400 p-1 justify-self-end"
                    title="Remove option"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      ))}
      {options && (
        <div className="text-[11px] text-gray-500">
          Hide an option with Active rather than removing it once leads use it, so existing leads keep their label.
        </div>
      )}
    </div>
  );
};

export default LeadTaxonomyPage;
